import type { Attendance } from "../api/types";
import { punchAt } from "./datetime";

export function localDateInTimezone(timeZone?: string | null): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone ?? undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}

export function shiftIsoDate(iso: string, days: number): string {
  const [year, month, day] = iso.split("-").map(Number);
  const value = new Date(Date.UTC(year!, month! - 1, day!));
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

/**
 * The two punches of an attendance correction.
 *
 * A correction names the day once and the clock times for it, so the times are
 * joined to that date here. A check-out earlier than the check-in belongs to
 * the next morning: an overnight shift closes the following day but still
 * belongs to the day it was rostered for -- which is the rule the API applies
 * when it checks the request.
 */
export function correctionPunches(
  date: string | null | undefined,
  clockIn: string | null | undefined,
  clockOut: string | null | undefined,
  timeZone?: string | null,
): { clockInAt: string | null; clockOutAt: string | null } {
  const zone = timeZone ?? undefined;
  const clockInAt = punchAt(date, clockIn, zone);
  let clockOutAt = punchAt(date, clockOut, zone);

  if (clockInAt && clockOutAt && clockOutAt < clockInAt && date) {
    clockOutAt = punchAt(shiftIsoDate(date, 1), clockOut, zone);
  }

  return { clockInAt, clockOutAt };
}

export function selfAttendanceQueryKey(today: string, employeeId?: string) {
  return ["attendance", "self", today, employeeId ?? "unresolved"] as const;
}

/**
 * Every cache key that reads attendance rows.
 *
 * A punch changes one row, and that row is read by the register, the employee
 * detail, the employee attendance panel and the desk boards. They are
 * invalidated together, so the work an employee submits shows up everywhere at
 * once and not only in the card that sent it.
 */
export const ATTENDANCE_QUERY_KEYS = [
  ["attendance"],
  ["emp-attendance"],
  ["employee-attendance"],
  ["manager-desk-attendance"],
  ["owner-desk-attendance"],
] as const;

/**
 * The register's cache key.
 *
 * It sits under the same `attendance` prefix as the employee's own card, so a
 * punch invalidates the register in the same call that refreshes the card: the
 * work an employee submits today must show up in the register without a
 * manual reload.
 */
export function registerQueryKey(employee: string | null) {
  return ["attendance", "register", employee ?? "none"] as const;
}

/**
 * The register's query for one employee.
 *
 * The Attendance page only ever asks for one person: nothing is fetched until
 * an employee is picked, and `employee` is always their id. `null` is kept for
 * the disabled query key, not as an all-employees view.
 */
export function registerQuery(employee: string | null) {
  return {
    employee: employee || undefined,
    ordering: "-work_date",
    page_size: 25,
  };
}

/** The Daily Work Report's own rule: both fields, trimmed, must be filled. */
export interface ReportFieldErrors {
  work_done?: string;
  learning?: string;
}

export function validateDailyReport(
  workDone: string,
  learning: string,
): ReportFieldErrors {
  const errors: ReportFieldErrors = {};
  if (!workDone.trim()) errors.work_done = "Work Done is required.";
  if (!learning.trim()) errors.learning = "Learning is required.";
  return errors;
}

/**
 * Has a usable Daily Work Report been saved for this day?
 *
 * The timestamp alone is not enough: a row written before the report fields
 * existed carries no report at all, and a half-written report must not unlock
 * check-out. This mirrors the server's rule, so the card never offers a
 * check-out the server will refuse.
 */
export function reportIsComplete(
  row:
    | {
        report_submitted_at?: string | null;
        work_done?: string | null;
        learning?: string | null;
      }
    | null
    | undefined,
): boolean {
  if (!row?.report_submitted_at) return false;
  return Boolean(row.work_done?.trim()) && Boolean(row.learning?.trim());
}

export interface SavedDailyReport {
  work_date: string;
  work_done: string;
  learning: string;
}

function reportMatches(
  saved: SavedDailyReport | null | undefined,
  values: SavedDailyReport,
): boolean {
  return Boolean(
    saved &&
      saved.work_date === values.work_date &&
      saved.work_done === values.work_done &&
      saved.learning === values.learning,
  );
}

/**
 * Save the Daily Work Report, then check out.
 *
 * Keeping the two calls in one workflow lets a checkout failure be retried
 * without writing the report again. The callback runs before checkout, so the
 * caller can remember a report that was saved even when the second request
 * fails. Punch operations never accept an employee id: a manager or owner can
 * only close their own day here.
 */
export async function saveReportThenCheckOut({
  workDate,
  workDone,
  learning,
  savedReport,
  submitReport,
  punchOut,
  onReportSaved,
}: {
  workDate: string;
  workDone: string;
  learning: string;
  savedReport?: SavedDailyReport | null;
  submitReport: (body: {
    work_date: string;
    work_done: string;
    learning: string;
  }) => Promise<Attendance>;
  punchOut: (body: { work_date: string }) => Promise<Attendance>;
  onReportSaved?: (report: SavedDailyReport) => void;
}): Promise<Attendance> {
  const report: SavedDailyReport = {
    work_date: workDate,
    work_done: workDone.trim(),
    learning: learning.trim(),
  };

  if (!reportMatches(savedReport, report)) {
    await submitReport(report);
    onReportSaved?.(report);
  }

  return punchOut({ work_date: workDate });
}

export interface SelfAttendance {
  row: Attendance | undefined;
  open: Attendance | undefined;
  overnight: boolean;
}

/**
 * Which shift My Desk should show.
 *
 * An open shift wins at any age: a forgotten check-out stays visible until the
 * employee closes it, even when the automatic stale-row sweep no longer runs.
 * The latest checked-out shift without its Daily Work Report comes next -- it
 * is the reason the next check-in is locked. Today's row follows, and older
 * shifts, complete or not, are history that must not hide today's check-in.
 */
export function pickSelfAttendance(
  rows: Attendance[],
  today: string,
): SelfAttendance {
  const latestFirst = [...rows].sort((a, b) =>
    b.work_date.localeCompare(a.work_date),
  );

  const open = latestFirst.find((row) => row.clock_in_at && !row.clock_out_at);
  if (open) {
    return {
      row: open,
      open,
      overnight: open.work_date !== today,
    };
  }

  // Only the latest worked shift can be reported: the server rejects a report
  // for an older day. An older missing report is history and must not hide the
  // check-in the server would allow.
  const latestWorked = latestFirst.find((row) => row.clock_in_at);
  if (
    latestWorked &&
    latestWorked.clock_out_at &&
    !reportIsComplete(latestWorked)
  ) {
    return {
      row: latestWorked,
      open: undefined,
      overnight: latestWorked.work_date !== today,
    };
  }

  const todayRows = latestFirst.filter((row) => row.work_date === today);
  if (todayRows.length > 0) {
    const completedWithReport = todayRows.find(
      (row) => row.clock_out_at && reportIsComplete(row),
    );
    if (completedWithReport) {
      return { row: completedWithReport, open: undefined, overnight: false };
    }
    const completed = todayRows.find((row) => row.clock_out_at);
    if (completed) {
      return { row: completed, open: undefined, overnight: false };
    }
    return { row: todayRows[0], open: undefined, overnight: false };
  }

  return { row: undefined, open: undefined, overnight: false };
}
